/**
 * Classification of a failed OAuth token refresh (#26615).
 *
 * `ionic-appauth` deletes the stored token response on *every* refresh error, no matter whether
 * the refresh token was actually rejected or the device simply had no network for a moment
 * (see `AuthService.refreshToken()` in the library). Both cases end up in the same
 * `RefreshFailed` action, so the app cannot tell an expired grant from a dead zone.
 *
 * This classifier restores that distinction:
 * - `invalid-grant`: the authorization server rejected the refresh token → logging out is correct.
 * - `transient`: network/timeout/server error → the session is still valid, retrying is correct.
 * - `unknown`: nothing recognisable. Treated as transient by callers on purpose: keeping a user
 *   logged in one cycle too long is cheap, throwing away a valid session is not.
 */
export type RefreshErrorKind = 'invalid-grant' | 'transient' | 'unknown';

/** OAuth2 error codes that mean the grant itself is gone (RFC 6749 §5.2). */
const TERMINAL_OAUTH_ERROR_CODES = [
  'invalid_grant',
  'invalid_client',
  'unauthorized_client',
  'unsupported_grant_type',
  'invalid_scope',
];

/** Markers of a failed request rather than a rejected grant. */
const TRANSIENT_MARKERS = [
  'network',
  'timeout',
  'timed out',
  'failed to fetch',
  'load failed',
  'offline',
  'connection',
  'econnreset',
  'econnrefused',
  'enotfound',
  'etimedout',
  'net::',
  'err_internet',
  'err_network',
  'err_connection',
  'unable to obtain server configuration',
];

const TRANSIENT_HTTP_STATUS = [408, 429, 500, 502, 503, 504];

function readStatus(error: unknown): number | null {
  const candidate = error as { status?: unknown; statusCode?: unknown; code?: unknown };
  for (const value of [candidate?.status, candidate?.statusCode, candidate?.code]) {
    if (typeof value === 'number') {
      return value;
    }
  }
  return null;
}

function stringify(error: unknown): string {
  if (error == null) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  const candidate = error as {
    message?: unknown;
    error?: unknown;
    errorDescription?: unknown;
    error_description?: unknown;
  };
  const parts = [candidate.message, candidate.error, candidate.errorDescription, candidate.error_description].filter(
    part => typeof part === 'string'
  );
  if (parts.length) {
    return parts.join(' ');
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Classifies a refresh failure. Never throws — a diagnostic must not add a second failure
 * on top of the one it is describing.
 */
export function classifyRefreshError(error: unknown): RefreshErrorKind {
  try {
    const text = stringify(error).toLowerCase();

    if (TERMINAL_OAUTH_ERROR_CODES.some(code => text.includes(code))) {
      return 'invalid-grant';
    }
    if (TRANSIENT_MARKERS.some(marker => text.includes(marker))) {
      return 'transient';
    }

    const status = readStatus(error);
    if (status != null) {
      if (TRANSIENT_HTTP_STATUS.includes(status)) {
        return 'transient';
      }
      if (status === 400 || status === 401) {
        // A 400/401 without a recognisable OAuth code: most likely a rejected grant, but the
        // body was not parseable — do not claim certainty.
        return 'unknown';
      }
    }

    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/** True when the session should be kept and the refresh retried. */
export function isRecoverableRefreshError(error: unknown): boolean {
  return classifyRefreshError(error) !== 'invalid-grant';
}
